import { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { MediaURL } from '../../template/imageCarouselTypes';
import type { AdjustBox, ImageCardsCropMeta } from './useImageCardsSplit';
import { useHeroCropMeta } from './useHeroCropMeta';

function errText(err: unknown): string {
  return (
    (err as { error?: string; message?: string })?.error ??
    (err as Error)?.message ??
    'Could not re-crop that image.'
  );
}

/**
 * "Adjust crop" for a hero image cut out of a pasted screenshot: the user moves
 * the one crop box over the kept source and the image is re-cropped from the
 * same pixels.
 */
export function useHeroImageCropAdjust(opts: {
  blockId: string;
  modelId: string;
  modelType: string;
  backendService: BackendService;
  primed?: ImageCardsCropMeta;
  onPrimedConsumed?: () => void;
  onRecropped: (imageUrl: MediaURL) => void;
}) {
  const { meta, update, forget } = useHeroCropMeta(opts);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async (boxes: AdjustBox[]) => {
    if (!meta?.source) return;
    const box = boxes.slice(0, 1);
    setBusy(true);
    setError(null);
    try {
      const { items } = await opts.backendService.recropImageCards(
        opts.modelType,
        opts.modelId,
        meta.source.path,
        box,
      );
      const imageUrl = items[0]?.imageUrl;
      if (!imageUrl) throw new Error('The re-crop returned no image.');
      update({ ...meta, boxes: box });
      setOpen(false);
      opts.onRecropped(imageUrl);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return {
    canAdjust: !!meta?.source,
    meta,
    open,
    busy,
    error,
    openDialog: () => setOpen(true),
    closeDialog: () => setOpen(false),
    forget,
    apply,
  };
}
